import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Workbook } from 'exceljs';

/**
 * Configuration for a single Excel import.
 */
export interface ExcelImportConfig {
  /** Target sheet name (matched case/space/hyphen-insensitively). */
  sheetName: string;
  /** Region start marker label, e.g. "Balance Sheet". */
  startMarker: string;
  /** Optional region end markers (next statement/section). */
  endMarkers?: string[];
}

/**
 * Pure parse result (no form mutation yet). The caller applies it
 * to its OWN model via applyPatch, so the model reference is always
 * current (avoids stale-reference issues across the async parse).
 */
export interface ExcelParseResult {
  excelData: Record<
    string,
    Array<{ section: string; byYear: Record<number, number> }>
  >;
  availableYears: number[];
}

/**
 * Result of applying a parse to the form.
 */
export interface ExcelImportResult {
  patchedRows: number;
  missingYears: string[];
  message: string;
}

interface DetectedSheet {
  worksheet: any;
  yearColumns: Record<number, number>;
  labelColumn: number;
  headerRow: number;
  startRow: number;
  endRow: number;
}

/**
 * Shared logic for importing financial values from an Excel file
 * and patching them into a section/row/year form model.
 *
 * Used by the Financial Data, Income Statement and Extra Terms
 * components (each only differs by sheet name and region markers).
 *
 * Split into two steps on purpose:
 *   parse()      -> read the file (async), no form mutation
 *   applyPatch() -> clear + patch the caller's OWN model (sync)
 * so the model reference is always the component's current one.
 */
@Injectable({ providedIn: 'root' })
export class ExcelImportService {
  private translate = inject(TranslateService);

  /**
   * Parse the file and build the label map for the target sheet.
   * `sections` is only read (for label/sheet detection); nothing
   * is mutated. Returns null if no matching sheet is found.
   */
  async parse(
    file: File,
    config: ExcelImportConfig,
    sections: any[],
  ): Promise<ExcelParseResult | null> {
    const buffer = await file.arrayBuffer();
    const workbook = new Workbook();
    await workbook.xlsx.load(buffer);

    const detected = this.detectSheet(workbook, config, sections);

    if (!detected) {
      return null;
    }

    const excelData = this.buildLabelMap(
      detected.worksheet,
      detected.yearColumns,
      detected.labelColumn,
      detected.startRow,
      detected.endRow,
    );

    const availableYears = Object.keys(detected.yearColumns).map((year) =>
      Number(year),
    );

    return { excelData, availableYears };
  }

  /**
   * Clear the form, then patch the parsed values for the selected
   * years. `sections` and `model` are mutated in place — callers
   * pass their OWN live references.
   */
  applyPatch(
    parsed: ExcelParseResult | null,
    sections: any[],
    model: Record<string, any>,
    years: string[],
  ): ExcelImportResult {
    if (!parsed) {
      return { patchedRows: 0, missingYears: [], message: this.translate.instant('EXCEL_UPLOAD.NO_DATA_FOUND') };
    }

    this.clear(sections, model, years);

    const patchedRows = this.patch(parsed.excelData, sections, model, years);

    const missingYears = years.filter(
      (year) => !parsed.availableYears.includes(Number(year)),
    );

    let message = '';

    if (patchedRows === 0) {
      message = this.translate.instant('EXCEL_UPLOAD.NO_DATA_FOUND');
    } else if (missingYears.length) {
      message = this.translate.instant('EXCEL_UPLOAD.NO_DATA_FOR_YEARS', { years: missingYears.join(', ') });
    }

    return { patchedRows, missingYears, message };
  }

  /**
   * Clear all values and selections from the form. Saved record
   * references are intentionally kept so a later save can delete
   * previously stored rows on the backend.
   */
  clear(sections: any[], model: Record<string, any>, years: string[]): void {
    (sections || []).forEach((section: any) => {
      section.checked = false;

      (section.rows || []).forEach((row: any) => {
        row.checked = false;

        years.forEach((year: string) => {
          delete model[this.getFieldKey(row.key, year)];
        });
      });
    });
  }

  getFieldKey(rowKey: string, year: string): string {
    return `${rowKey}_${year}`;
  }

  /**
   * Same as normalizeLabel, but also strips a leading numbering
   * prefix (e.g. "4. Current Liabilities" -> "current liabilities")
   * so section headers from the sheet match the form's section
   * titles, which are unnumbered.
   */
  private normalizeSectionName(value: any): string {
    return this.normalizeLabel(value).replace(/^\d+[.)]?\s*/, '');
  }

  normalizeLabel(value: any): string {
    return String(value ?? '')
      .replace(/[-–—]/g, ' ')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
  }

  // ---------------------------------------------------------------

  private patch(
    excelData: Record<
      string,
      Array<{ section: string; byYear: Record<number, number> }>
    >,
    sections: any[],
    model: Record<string, any>,
    years: string[],
  ): number {
    let patchedRows = 0;

    (sections || []).forEach((section: any) => {
      let sectionHasData = false;

      (section.rows || []).forEach((row: any) => {
        const entries = excelData[this.normalizeLabel(row.label)];

        if (!entries || !entries.length) {
          return;
        }

        /*
         * Prefer the entry whose section matches this form section.
         * If the label is unique in the file, fall back to it. If it
         * is a duplicate with no section match, skip rather than guess.
         */
        const sectionKey = this.normalizeLabel(section.title);
        const sectionMatches = entries.filter(
          (entry) => entry.section === sectionKey,
        );

        const chosen = sectionMatches.length
          ? sectionMatches[0]
          : entries.length === 1
            ? entries[0]
            : null;

        if (!chosen) {
          return;
        }

        let rowHasData = false;

        years.forEach((year: string) => {
          const value = chosen.byYear[Number(year)];

          if (value !== undefined) {
            model[this.getFieldKey(row.key, year)] = value;
            rowHasData = true;
          }
        });

        if (rowHasData) {
          row.checked = true;
          sectionHasData = true;
          patchedRows++;
        }
      });

      if (sectionHasData) {
        section.checked = true;
      }
    });

    return patchedRows;
  }

  private detectSheet(
    workbook: Workbook,
    config: ExcelImportConfig,
    sections: any[],
  ): DetectedSheet | null {
    const formLabels = this.collectFormLabels(sections);

    if (!formLabels.size) {
      return null;
    }

    const targetName = this.normalizeLabel(config.sheetName);
    const allSheets: any[] = workbook.worksheets || [];

    const namedSheet = allSheets.find(
      (worksheet: any) => this.normalizeLabel(worksheet.name) === targetName,
    );

    const candidates: any[] = namedSheet
      ? [namedSheet]
      : allSheets.filter(
          (worksheet: any) =>
            this.normalizeLabel(worksheet.name) !== 'financial analysis',
        );

    let best: DetectedSheet | null = null;
    let bestScore = 0;

    candidates.forEach((worksheet: any) => {
      const header = this.findYearHeaderRow(worksheet);

      if (!header) {
        return;
      }

      const labelInfo = this.findLabelColumn(
        worksheet,
        header.headerRow,
        formLabels,
      );

      if (labelInfo.column && labelInfo.score > bestScore) {
        bestScore = labelInfo.score;

        const region = this.findRegion(
          worksheet,
          labelInfo.column,
          header.headerRow,
          this.normalizeLabel(config.startMarker),
          (config.endMarkers || []).map((marker) =>
            this.normalizeLabel(marker),
          ),
        );

        best = {
          worksheet,
          yearColumns: header.yearColumns,
          labelColumn: labelInfo.column,
          headerRow: header.headerRow,
          startRow: region.startRow,
          endRow: region.endRow,
        };
      }
    });

    return bestScore > 0 ? best : null;
  }

  private collectFormLabels(sections: any[]): Set<string> {
    const labels = new Set<string>();

    (sections || []).forEach((section: any) => {
      (section.rows || []).forEach((row: any) => {
        labels.add(this.normalizeLabel(row.label));
      });
    });

    return labels;
  }

  private findYearHeaderRow(
    worksheet: any,
  ): { headerRow: number; yearColumns: Record<number, number> } | null {
    let bestRow = 0;
    let bestCount = 0;
    let bestMap: Record<number, number> = {};

    const maxScan = Math.min(worksheet.rowCount || 0, 25);

    for (let r = 1; r <= maxScan; r++) {
      const row = worksheet.getRow(r);
      const map: Record<number, number> = {};

      row.eachCell(
        { includeEmpty: false },
        (cell: any, colNumber: number) => {
          const year = this.extractYear(cell.value);

          if (year) {
            map[year] = colNumber;
          }
        },
      );

      const count = Object.keys(map).length;

      if (count >= 2 && count > bestCount) {
        bestCount = count;
        bestRow = r;
        bestMap = map;
      }
    }

    return bestRow ? { headerRow: bestRow, yearColumns: bestMap } : null;
  }

  private findLabelColumn(
    worksheet: any,
    headerRow: number,
    formLabels: Set<string>,
  ): { column: number; score: number } {
    let bestColumn = 0;
    let bestScore = 0;

    const maxCol = Math.min(worksheet.columnCount || 0, 10);
    const maxRow = Math.min(worksheet.rowCount || 0, 500);

    for (let c = 1; c <= maxCol; c++) {
      let score = 0;

      for (let r = headerRow + 1; r <= maxRow; r++) {
        const value = this.readCellValue(worksheet.getRow(r).getCell(c).value);

        if (
          typeof value === 'string' &&
          formLabels.has(this.normalizeLabel(value))
        ) {
          score++;
        }
      }

      if (score > bestScore) {
        bestScore = score;
        bestColumn = c;
      }
    }

    return { column: bestColumn, score: bestScore };
  }

  private findRegion(
    worksheet: any,
    labelColumn: number,
    headerRow: number,
    startMarker: string,
    endMarkers: string[],
  ): { startRow: number; endRow: number } {
    const endSet = new Set<string>(endMarkers);
    const maxRow = worksheet.rowCount || 0;
    let startRow = 0;

    for (let r = headerRow; r <= maxRow; r++) {
      const label = this.normalizeLabel(
        this.readCellValue(worksheet.getRow(r).getCell(labelColumn).value),
      );

      if (!startRow) {
        if (label === startMarker) {
          startRow = r;
        }

        continue;
      }

      if (endSet.has(label)) {
        return { startRow, endRow: r - 1 };
      }
    }

    if (startRow) {
      return { startRow, endRow: maxRow };
    }

    return { startRow: headerRow, endRow: maxRow };
  }

  private buildLabelMap(
    worksheet: any,
    yearColumns: Record<number, number>,
    labelColumn: number,
    startRow: number,
    endRow: number,
  ): Record<string, Array<{ section: string; byYear: Record<number, number> }>> {
    const map: Record<
      string,
      Array<{ section: string; byYear: Record<number, number> }>
    > = {};

    let currentSection = '';
    const seenSectionHeaders = new Set<string>();

    for (let r = startRow; r <= endRow; r++) {
      const rawLabel = this.readCellValue(
        worksheet.getRow(r).getCell(labelColumn).value,
      );

      if (typeof rawLabel !== 'string' || !rawLabel.trim()) {
        continue;
      }

      const byYear: Record<number, number> = {};

      Object.keys(yearColumns).forEach((yearKey: string) => {
        const year = Number(yearKey);
        const column = yearColumns[year];
        const value = this.readCellValue(
          worksheet.getRow(r).getCell(column).value,
        );
        const numericValue = Number(value);

        if (value !== null && value !== '' && Number.isFinite(numericValue)) {
          byYear[year] = Math.round(numericValue);
        }
      });

      // A row with no values is treated as a section header, unless it is
      // a repeated sub-header caption (e.g. "Field in your form" appears
      // under every section) — the first occurrence would otherwise keep
      // overwriting currentSection and every later section would collapse
      // into that one bogus name.
      if (!Object.keys(byYear).length) {
        const normalizedHeader = this.normalizeSectionName(rawLabel);

        if (!seenSectionHeaders.has(normalizedHeader)) {
          currentSection = normalizedHeader;
          seenSectionHeaders.add(normalizedHeader);
        }

        continue;
      }

      const key = this.normalizeLabel(rawLabel);

      if (!map[key]) {
        map[key] = [];
      }

      map[key].push({ section: currentSection, byYear });
    }

    return map;
  }

  private extractYear(raw: any): number | null {
    const value = this.readCellValue(raw);

    if (value instanceof Date) {
      return value.getFullYear();
    }

    if (typeof value === 'number' && value >= 1990 && value <= 2100) {
      return Math.trunc(value);
    }

    if (typeof value === 'string') {
      const text = value.trim();
      const fullYear = text.match(/(19|20)\d{2}/);

      if (fullYear) {
        return Number(fullYear[0]);
      }

      const monthYear = text.match(/[A-Za-z]{3,}[-\s]?(\d{2})$/);

      if (monthYear) {
        return 2000 + Number(monthYear[1]);
      }
    }

    return null;
  }

  private readCellValue(value: any): any {
    if (value === null || value === undefined) {
      return null;
    }

    if (value instanceof Date) {
      return value;
    }

    if (typeof value === 'object') {
      if ('result' in value) {
        return value.result;
      }

      if ('text' in value) {
        return value.text;
      }

      if ('richText' in value) {
        return (value.richText || [])
          .map((part: any) => part.text)
          .join('');
      }

      if ('error' in value) {
        return null;
      }
    }

    return value;
  }
}
